import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { graphqlRequest } from './graphql'
import type {
  KeycloakUser,
  CreateKeycloakUserInput,
  UpdateKeycloakUserInput,
} from '@shinkansen/graphql'

// Re-export types from shared package for convenience
export type { KeycloakUser, CreateKeycloakUserInput, UpdateKeycloakUserInput }

/**
 * User role types from WorkOS-backed identity
 * Note: These are the application-specific role names, not from GraphQL schema
 */
export type UserRole = 'admin' | 'customer' | 'driver' | 'operations_manager'

/**
 * User interface for admin list display
 * Extended from the generated GraphQL user shape with typed roles
 */
export interface User {
  id: string
  /** Derived local UUID — use for FK values like createDriver.userId. `id` is the raw WorkOS subject. */
  localId: string
  username: string
  email: string
  firstName: string
  lastName: string
  roles: UserRole[]
  enabled: boolean
  createdAt: string
}

/**
 * GraphQL response type for the current schema-named user query.
 * Hikari maps this resolver to WorkOS.
 */
type KeycloakUserResponse = KeycloakUser

/**
 * Hook to fetch all users from the WorkOS-backed backend GraphQL API.
 * Returns TanStack Query result with users array.
 *
 * @returns TanStack Query result with User[] data
 */
export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: async (): Promise<User[]> => {
      const result = await graphqlRequest<{ keycloakUsers: KeycloakUserResponse[] }>(`
        query KeycloakUsers {
          keycloakUsers {
            id
            localId
            username
            email
            firstName
            lastName
            roles
            enabled
            createdAt
          }
        }
      `)
      
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      
      // Transform response to match User interface
      return (result.data?.keycloakUsers ?? []).map((u) => ({
        id: u.id,
        localId: u.localId,
        username: u.username,
        email: u.email ?? '',
        firstName: u.firstName ?? '',
        lastName: u.lastName ?? '',
        roles: u.roles as UserRole[],
        enabled: u.enabled,
        createdAt: u.createdAt ?? new Date().toISOString(),
      }))
    },
  })
}

/**
 * Get the external user-management console URL.
 *
 * Users are managed in WorkOS (User Management dashboard). The in-app admin
 * users page drives the WorkOS API through hikari; this link points operators
 * at the WorkOS dashboard for anything not exposed in-app.
 */
export function getWorkosAdminUrl(): string {
  return import.meta.env.VITE_WORKOS_ADMIN_URL || 'https://dashboard.workos.com'
}

// ============== Mutation Types ==============

/**
 * Input for creating a new user - alias for shared type
 */
export type CreateUserInput = CreateKeycloakUserInput

/**
 * Input for updating a user - alias for shared type
 */
export type UpdateUserInput = UpdateKeycloakUserInput

// ============== Mutation Hooks ==============

/**
 * Hook to create a new WorkOS-backed user through the current schema mutation
 */
export function useCreateUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateUserInput) => {
      const result = await graphqlRequest<{ createKeycloakUser: KeycloakUserResponse }>(`
        mutation CreateKeycloakUser($input: CreateKeycloakUserInput!) {
          createKeycloakUser(input: $input) {
            id
            username
            email
            firstName
            lastName
            roles
            enabled
            createdAt
          }
        }
      `, { input })
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.createKeycloakUser
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

/**
 * Hook to update an existing WorkOS-backed user through the current schema mutation
 */
export function useUpdateUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: UpdateUserInput) => {
      const result = await graphqlRequest<{ updateKeycloakUser: KeycloakUserResponse }>(`
        mutation UpdateKeycloakUser($input: UpdateKeycloakUserInput!) {
          updateKeycloakUser(input: $input) {
            id
            username
            email
            firstName
            lastName
            roles
            enabled
            createdAt
          }
        }
      `, { input })
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.updateKeycloakUser
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

/**
 * Hook to enable or disable a WorkOS-backed user
 */
export function useToggleUserEnabled() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ userId, enabled }: { userId: string; enabled: boolean }) => {
      const mutation = enabled ? 'enableKeycloakUser' : 'disableKeycloakUser'
      const result = await graphqlRequest<{ [key: string]: boolean }>(`
        mutation ToggleUser($userId: String!) {
          ${mutation}(userId: $userId)
        }
      `, { userId })
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

/**
 * Hook to fetch available WorkOS role slugs through the current schema query
 */
export function useAvailableRoles() {
  return useQuery({
    queryKey: ['keycloakRoles'],
    queryFn: async () => {
      const result = await graphqlRequest<{ keycloakRoles: string[] }>(`
        query KeycloakRoles {
          keycloakRoles
        }
      `)
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.keycloakRoles ?? []
    },
  })
}
